import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { formatearFecha, validarRangoFechas } from '../../helpers/dateValidation'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import GenericSelect from '../reutilizables/genericSelect/GenericSelect'
import ListaVentaFormModal from '../reutilizables/listaVenta/ListaVentaFormModal'
import {
  buildListaVentaInitialValues,
  validateListaVentaForm
} from '../reutilizables/listaVenta/listaVentaFormUtils'
import * as listaVentaService from '../servicios/listaVentaService'
import styles from './ListaDeVentas.module.css'

const ListaDeVentas = ({
  onChangeState,
  onListaVentaChange,
  idVendedor,
  vieneDeAdministrar,
  actualizarPantallaDetalle,
  value
}) => {
  const [listas, setListas] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [nuevaLista, setNuevaLista] = useState(buildListaVentaInitialValues())
  const [errores, setErrores] = useState({})

  useEffect(() => {
    const obtenerListas = async () => {
      try {
        const listasRtn = await listaVentaService.buscarListaPorVendedor(idVendedor)
        setListas(
          listasRtn.data
            .filter(
              (element) =>
                !element.vigenciaHasta ||
                validarRangoFechas(new Date(), element.vigenciaHasta, 'day')
            )
            .map((element) => ({
              value: element.idListaVenta,
              label: element.nombre,
              vigenciaDesde: formatearFecha(element.vigenciaDesde, 'YYYY-MM-DD'),
              vigenciaHasta: formatearFecha(element.vigenciaHasta, 'YYYY-MM-DD')
            }))
        )
      } catch (error) {
        const mensaje = 'Ocurrió un error al cargar las listas de venta.'
        toastErrorLog(error, mensaje, 'listaVenta')
      }
    }
    obtenerListas()
  }, [idVendedor, actualizarPantallaDetalle])

  const abrirModal = () => {
    setNuevaLista(buildListaVentaInitialValues())
    setErrores({})
    setShowModal(true)
  }

  const cerrarModal = () => {
    setShowModal(false)
    setErrores({})
  }

  const guardarLista = async () => {
    try {
      const nuevosErrores = validateListaVentaForm({
        values: nuevaLista,
        listas
      })

      setErrores(nuevosErrores)
      if (Object.keys(nuevosErrores).length > 0) {
        return
      }

      const lista = {
        idVendedor,
        nombre: nuevaLista.nombre.trim(),
        idListaVentaEstado: 2,
        vigenciaDesde: nuevaLista.vigenciaDesde,
        vigenciaHasta: nuevaLista.vigenciaHasta,
        ofertaDesde: null,
        ofertaHasta: null,
        porcDcto: null
      }

      const nuevaListaCreada = await listaVentaService.insertarLista(lista)
      onChangeState('idListaVenta', nuevaListaCreada.data.idListaVenta)
      const listasActualizadas = await refrescasListas()
      const listaSeleccionada = listasActualizadas.find(
        (item) => Number(item.value) === Number(nuevaListaCreada.data.idListaVenta)
      )
      if (onListaVentaChange) {
        onListaVentaChange(listaSeleccionada || null)
      }
      cerrarModal()
    } catch (error) {
      const mensaje = 'Ocurrió un error al guardar la lista.'
      toastErrorLog(error, mensaje, 'listaVenta')
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNuevaLista((prev) => ({
      ...prev,
      [name]: value
    }))

    if (errores[name]) {
      setErrores((prev) => {
        const nuevos = { ...prev }
        delete nuevos[name]
        return nuevos
      })
    }
  }

  const refrescasListas = async () => {
    try {
      const listasRtn = await listaVentaService.buscarListaPorVendedor(idVendedor)
      const listasMapeadas = listasRtn.data
        .filter(
          (lista) =>
            !lista.vigenciaHasta || validarRangoFechas(new Date(), lista.vigenciaHasta, 'day')
        )
        .map((lista) => ({
          value: lista.idListaVenta,
          label: lista.nombre,
          vigenciaDesde: formatearFecha(lista.vigenciaDesde, 'YYYY-MM-DD'),
          vigenciaHasta: formatearFecha(lista.vigenciaHasta, 'YYYY-MM-DD')
        }))

      setListas(listasMapeadas)
      return listasMapeadas
    } catch (error) {
      const mensaje = 'Ocurrió un error al refrescar las listas de venta.'
      toastErrorLog(error, mensaje, 'listaVenta')
      return []
    }
  }

  const handleSelectLista = (nuevoValor) => {
    onChangeState('idListaVenta', nuevoValor)
    if (!onListaVentaChange) {
      return
    }

    const listaSeleccionada = listas.find((item) => Number(item.value) === Number(nuevoValor))
    onListaVentaChange(listaSeleccionada || null)
  }

  return (
    <>
      {!vieneDeAdministrar && (
        <p className="descripcion-cuadro-publicacion">
          Indica si el artículo pertenece a una Lista de Ventas.
        </p>
      )}
      <div className="row" id="">
        <div className={`col-12 col-md-6 mb-3 ${styles.selectorColumna}`}>
          <div className={styles.selectorWrapper}>
            <GenericSelect
              options={listas}
              value={value || ''}
              label="Elegir Lista:"
              id="idListaVenta"
              className={styles.selectListaVenta}
              onChange={handleSelectLista}
            />
            {vieneDeAdministrar && Number(value) > 0 && (
              <button
                type="button"
                className={styles.botonEliminarLista}
                title="Eliminar lista de la publicación"
                onClick={() => handleSelectLista(0)}
              >
                <i className="bi bi-trash" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
        <div className={styles.crearListaVenta}>
          <button type="button" className={styles.botonCrearLista} onClick={abrirModal}>
            <span>Crear una nueva lista</span>
            <i className={`fa fa-plus ${styles.iconoPlus}`} aria-hidden="true" />
          </button>
        </div>
      </div>

      <ListaVentaFormModal
        show={showModal}
        title="Crear lista de venta"
        values={nuevaLista}
        errores={errores}
        onChange={handleInputChange}
        onClose={cerrarModal}
        onConfirm={guardarLista}
      />
    </>
  )
}

ListaDeVentas.propTypes = {
  onChangeState: PropTypes.func.isRequired,
  onListaVentaChange: PropTypes.func,
  idVendedor: PropTypes.number.isRequired,
  vieneDeAdministrar: PropTypes.bool,
  actualizarPantallaDetalle: PropTypes.bool,
  value: PropTypes.number.isRequired
}

export default ListaDeVentas
